import React, { useState } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';

export default function CompactLeadForm() {
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    setMsg("");

    if (!email.trim()) {
      setStatus("error");
      setMsg("Please enter your email address.");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }

    if (!gdprConsent) {
      setStatus("error");
      setMsg("Please consent to receiving emails.");
      return;
    }

    const payload = {
      email: email,
      turnstileToken: "", // No Turnstile for sidebar version to keep it simple
      website: "", // honeypot
      gdprConsent: gdprConsent,
      consentTimestamp: new Date().toISOString()
    };

    try {
      // Development mode simulation
      if (process.env.NODE_ENV === 'development') {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Simulate success response for development
        setStatus("success");
        setMsg("✅ Success! (Dev mode)");
        setEmail("");
        setGdprConsent(false);
        console.log("Development mode - sidebar form payload:", payload);
        return;
      }

      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus("success");
        setMsg("Thanks! Check your inbox.");
        setEmail("");
        setGdprConsent(false);
      } else {
        setStatus("error");
        setMsg(data.message || "Something went wrong.");
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        setStatus("success");
        setMsg("✅ Success! (Dev mode)");
        setEmail("");
        setGdprConsent(false);
        console.log("Development mode - sidebar form would submit:", payload);
      } else {
        setStatus("error");
        setMsg("Network error. Please try again.");
      }
    }
  }

  return (
    <Box 
      sx={{ 
        p: 2, 
        bgcolor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}
    >
      <Typography variant="subtitle2" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
        📧 Stay Updated
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Get UK tax updates & salary tips
      </Typography>
      
      <Box component="form" onSubmit={onSubmit} noValidate>
        <TextField
          fullWidth
          size="small"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          InputProps={{
            sx: {
              fontSize: '0.875rem',
              '& input': {
                padding: '8px 12px',
                '&::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7,
                  fontSize: '0.875rem',
                  transform: 'none !important',
                  transition: 'opacity 0.2s ease-in-out',
                  position: 'static !important'
                },
                '&:focus::placeholder': {
                  opacity: 0.4,
                  transform: 'none !important'
                }
              }
            }
          }}
          sx={{ 
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.400'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.500'
              }
            },
            // Disable any label animations and transforms
            '& .MuiInputLabel-root': {
              transform: 'none !important',
              position: 'static !important'
            },
            // Ensure no transforms are applied to any child elements
            '& *': {
              '&::placeholder': {
                transform: 'none !important'
              }
            }
          }}
          error={status === "error"}
        />

        {/* Honeypot */}
        <input 
          name="website" 
          tabIndex="-1" 
          autoComplete="off" 
          style={{ display: "none" }} 
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)}
              size="small"
              sx={{
                color: 'primary.600',
                '&.Mui-checked': {
                  color: 'primary.600'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 18
                }
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'text.primary', fontSize: '0.8rem', lineHeight: 1.4 }}>
              I consent to emails. 
              <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5, color: 'primary.600', fontWeight: 'medium' }}>
                Privacy policy
              </Link>
            </Typography>
          }
          sx={{ mb: 1.5, alignItems: 'flex-start', mt: 0.5 }}
        />

        <Button
          type="submit"
          fullWidth
          size="small"
          variant="contained"
          disabled={status === "loading"}
          sx={{ 
            mb: 1,
            fontSize: '0.75rem',
            backgroundColor: 'primary.500',
            '&:hover': {
              backgroundColor: 'primary.600'
            },
            '&:disabled': {
              backgroundColor: 'neutral.300'
            }
          }}
        >
          {status === "loading" ? "Subscribing..." : "Subscribe"}
        </Button>

        {status === "success" && (
          <Alert severity="success" sx={{ fontSize: '0.75rem', py: 0 }}>
            {msg}
          </Alert>
        )}
        
        {status === "error" && (
          <Alert severity="error" sx={{ fontSize: '0.75rem', py: 0 }}>
            {msg}
          </Alert>
        )}
      </Box>
    </Box>
  );
}